import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout';
import { ChevronRight, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../api/examApi';
import { DASH, fmtPct, fmtAge, pctTone } from '../lib/reportFormat';
import { Th, HowToReadPanel } from '../components/reports/ReportTooltip';

// The 8 locked columns (docs/reporting-metrics-spec.md §10.3), in order.
// One source for both the table headers and the "How to read this page"
// panel below — a column's display label is written once, here, not
// hand-typed separately in two JSX spots that could drift apart.
//
// §10.1: the SLA window is a single configurable constant
// (GRADING_SLA_DAYS), served by the API alongside metric_definitions
// (data.grading_sla_days) — never hardcoded here. `slaDays` is null only
// for the brief window before the first response lands, in which case the
// label degrades to a window-agnostic phrase rather than guessing a number.
function buildReportColumns(slaDays) {
  const slaWindowLabel = slaDays == null ? 'SLA window' : `${slaDays} days`;
  return [
    { id: 'tests_given', label: 'Tests given' },
    { id: 'class_average', label: 'Class average' },
    { id: 'students_assigned', label: 'Students assigned' },
    { id: 'submissions_received', label: 'Submissions received — on-time / late / none' },
    { id: 'graded_within_sla', label: `Graded within ${slaWindowLabel}` },
    { id: 'overdue_grading', label: 'Overdue grading' },
    { id: 'attendance_on_time', label: 'Attendance entered on time' },
    { id: 'exams_overdue', label: 'Exams left open past deadline' },
  ];
}

// Weighted across a faculty's exams in range — never an average-of-percentiles
// (spec §1.4 forbids that for cohort rank; this is class average per exam,
// the metric §1.4 explicitly names as the correct group-level substitute),
// and never on its own without the spread that came with it.
function summarizeClassAverage(examRows) {
  if (!examRows || examRows.length === 0) return null;
  const totalN = examRows.reduce((sum, e) => sum + e.n, 0);
  if (totalN === 0) return null;
  const weightedAvg = examRows.reduce((sum, e) => sum + e.avg_pct * e.n, 0) / totalN;
  const minPct = Math.min(...examRows.map((e) => e.min_pct));
  const maxPct = Math.max(...examRows.map((e) => e.max_pct));
  return { avg: weightedAvg, min: minPct, max: maxPct, examCount: examRows.length, studentCount: totalN };
}

const todayIso = () => new Date().toISOString().slice(0, 10);

function PunctualityBar({ funnel }) {
  const { assigned, on_time, late, no_upload } = funnel;
  if (!assigned) {
    return <span className="text-xs text-slate-400">No exams assigned</span>;
  }
  const pct = (n) => (100 * n) / assigned;
  return (
    <div className="w-40">
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
        {on_time > 0 && (
          <div style={{ width: `${pct(on_time)}%` }} className="bg-emerald-500" title={`${on_time} on time`} />
        )}
        {late > 0 && (
          <div style={{ width: `${pct(late)}%` }} className="bg-amber-500" title={`${late} late`} />
        )}
        {no_upload > 0 && (
          <div style={{ width: `${pct(no_upload)}%` }} className="bg-slate-300 dark:bg-slate-500" title={`${no_upload} none`} />
        )}
      </div>
      <div className="mt-1 flex flex-wrap gap-x-2 text-[11px] text-slate-500 dark:text-slate-400">
        <span><span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 mr-1" />{on_time} on-time</span>
        <span><span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500 mr-1" />{late} late</span>
        <span><span className="inline-block h-1.5 w-1.5 rounded-full bg-slate-400 mr-1" />{no_upload} none</span>
      </div>
      {/* Column 3 (Students assigned) is DISTINCT STUDENTS; this bar counts
          ASSIGNMENTS — with more than one test in range these legitimately
          differ. Spell out the bar's own denominator so that isn't read as
          a bug (docs/reporting-metrics-spec.md §10, build note). */}
      <p className="mt-1 text-[11px] text-slate-400">of {assigned} submission{assigned === 1 ? '' : 's'} expected</p>
    </div>
  );
}

function FacultyRow({ row, onOpen, slaDays }) {
  const noActivity = row.exams_created === 0 && row.funnel.assigned === 0;
  const classAvg = summarizeClassAverage(row.class_average_per_exam);
  const overdueCount = row.exams_past_deadline_still_open.length;
  const maxDaysPast = overdueCount ? Math.max(...row.exams_past_deadline_still_open.map((o) => o.days_past)) : null;

  return (
    <>
      <tr
        onClick={onOpen}
        className={`cursor-pointer transition-colors ${noActivity ? 'opacity-60' : ''} hover:bg-slate-50 dark:hover:bg-slate-700/30`}
        data-testid={`faculty-report-row-${row.faculty_id}`}
      >
        {/* Faculty identity — not one of the 8 metric columns */}
        <td className="px-4 py-3 align-top">
          <div className="flex items-center gap-1.5">
            <ChevronRight size={15} className="text-slate-400 shrink-0" />
            <div>
              <p className="font-medium text-slate-900 dark:text-white">{row.faculty_name}</p>
              {noActivity && (
                <p className="text-xs text-slate-400 mt-0.5">No activity in this range</p>
              )}
            </div>
          </div>
        </td>

        {/* 1. Tests given */}
        <td className="px-4 py-3 align-top text-slate-700 dark:text-slate-300">
          {row.exams_created}
        </td>

        {/* 2. Class average (with range and n) */}
        <td className="px-4 py-3 align-top">
          {classAvg ? (
            <>
              <span className="text-slate-800 dark:text-slate-200 font-medium">{fmtPct(classAvg.avg)}</span>
              <p className="text-xs text-slate-400">{fmtPct(classAvg.min, 0)}&ndash;{fmtPct(classAvg.max, 0)} range &middot; n={classAvg.studentCount}</p>
            </>
          ) : (
            <span className="text-slate-400">{DASH}</span>
          )}
        </td>

        {/* 3. Students assigned — DISTINCT students, not assignment rows */}
        <td className="px-4 py-3 align-top text-slate-700 dark:text-slate-300">
          {row.students_assigned}
        </td>

        {/* 4. Submissions received — on-time / late / none */}
        <td className="px-4 py-3 align-top">
          <PunctualityBar funnel={row.funnel} />
        </td>

        {/* 5. Graded within {slaDays} days (%) */}
        <td className="px-4 py-3 align-top">
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${pctTone(row.graded_within_sla.rate_pct)}`}>
            {fmtPct(row.graded_within_sla.rate_pct)}
          </span>
          <p className="text-xs text-slate-400 mt-0.5">n={row.graded_within_sla.graded_n} graded in range</p>
        </td>

        {/* 6. Overdue grading (count) */}
        <td className="px-4 py-3 align-top">
          <span className={`font-medium ${row.overdue_grading_count > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-700 dark:text-slate-300'}`}>
            {row.overdue_grading_count}
          </span>
          <p className="text-xs text-slate-400">past {slaDays == null ? 'the SLA window' : `${slaDays} days`}, right now</p>
        </td>

        {/* 7. Attendance entered on time (%) */}
        <td className="px-4 py-3 align-top">
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${pctTone(row.attendance_same_day_rate.rate_pct)}`}>
            {fmtPct(row.attendance_same_day_rate.rate_pct)}
          </span>
          <p className="text-xs text-slate-400 mt-0.5">
            {row.attendance_same_day_rate.marks > 0 ? `of ${row.attendance_same_day_rate.marks} attendance entries` : 'no attendance entries'}
          </p>
        </td>

        {/* 8. Exams left open past deadline */}
        <td className="px-4 py-3 align-top">
          {overdueCount > 0 ? (
            <>
              <span className="font-medium text-rose-600 dark:text-rose-400">{overdueCount}</span>
              <p className="text-xs text-slate-400">up to {fmtAge(maxDaysPast)} late</p>
            </>
          ) : (
            <span className="text-slate-400">{DASH}</span>
          )}
        </td>
      </tr>
    </>
  );
}

export default function AdminReportsPage() {
  const navigate = useNavigate();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [rows, setRows] = useState([]);
  const [definitions, setDefinitions] = useState(null);
  const [slaDays, setSlaDays] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((params) => {
    setLoading(true);
    adminApi
      .getFacultyReport(params)
      .then(({ data }) => {
        setDateFrom(data.date_from);
        setDateTo(data.date_to);
        setRows(data.faculty);
        setDefinitions(data.metric_definitions || null);
        setSlaDays(data.grading_sla_days ?? null);
      })
      .catch(() => toast.error('Failed to load the faculty report'))
      .finally(() => setLoading(false));
  }, []);

  const reportColumns = useMemo(() => buildReportColumns(slaDays), [slaDays]);

  // Initial load: no params, so the backend applies its own current-month
  // default (§ Build order Step 3 brief) — the pickers are then filled in
  // from what the server actually used, not guessed client-side.
  useEffect(() => {
    load({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyRange = () => {
    if (!dateFrom || !dateTo) return;
    load({ date_from: dateFrom, date_to: dateTo });
  };

  const totals = useMemo(() => {
    const withActivity = rows.filter((r) => r.exams_created > 0 || r.funnel.assigned > 0).length;
    const totalOverdueGrading = rows.reduce((sum, r) => sum + r.overdue_grading_count, 0);
    const totalOverdueExams = rows.reduce((sum, r) => sum + r.exams_past_deadline_still_open.length, 0);
    return { withActivity, totalOverdueGrading, totalOverdueExams, facultyCount: rows.length };
  }, [rows]);

  return (
    <DashboardLayout title="Faculty Report">
      <div className="animate-fade-in space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-lg bg-primary/10 dark:bg-accent-cool/10 p-2 text-primary dark:text-accent-cool">
              <BarChart3 size={20} />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
                Faculty Report
              </h2>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                How each faculty member is working, for any date range. Read-only &mdash; click a row to
                see per-exam detail.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">From</label>
              <input
                type="date"
                value={dateFrom}
                max={dateTo || todayIso()}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool"
                data-testid="report-date-from"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">To</label>
              <input
                type="date"
                value={dateTo}
                min={dateFrom}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool"
                data-testid="report-date-to"
              />
            </div>
            <button
              type="button"
              onClick={applyRange}
              disabled={!dateFrom || !dateTo}
              className="px-4 py-2 rounded-md bg-primary dark:bg-accent-cool text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
              data-testid="report-apply-range"
            >
              Apply
            </button>

            {!loading && rows.length > 0 && (
              <p className="text-sm text-slate-500 dark:text-slate-400 ml-auto">
                {totals.withActivity} of {totals.facultyCount} faculty active &middot; {totals.totalOverdueGrading} answer sheet(s) overdue for grading &middot; {totals.totalOverdueExams} exam(s) overdue
              </p>
            )}
          </div>
        </div>

        <HowToReadPanel definitions={definitions} columns={reportColumns} />

        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden shadow-sm">
          {loading ? (
            <div className="animate-pulse">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center p-4 border-b border-slate-100 dark:border-slate-800">
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/5 mr-4"></div>
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/5 mr-4"></div>
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/5 mr-4"></div>
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/5"></div>
                </div>
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
              <BarChart3 size={28} className="text-slate-300 dark:text-slate-600" />
              No faculty found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[1200px]">
                <thead>
                  <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                    <th className="px-4 py-3 text-left font-medium text-slate-500">Faculty</th>
                    {reportColumns.map((col) => (
                      <Th key={col.id} defId={col.id} definitions={definitions}>{col.label}</Th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {rows.map((row) => (
                    <FacultyRow
                      key={row.faculty_id}
                      row={row}
                      slaDays={slaDays}
                      onOpen={() => navigate(`/admin/reports/faculty/${row.faculty_id}?date_from=${dateFrom}&date_to=${dateTo}`)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
